import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import type { Merchant, MerchantStatus } from '@/types/merchant';

interface MerchantInfoProps {
  merchant: Merchant;
}

const statusVariant: Record<MerchantStatus, 'success' | 'warning' | 'destructive'> = {
  active: 'success',
  suspended: 'destructive',
  deactivated: 'warning',
};

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function MerchantInfo({ merchant }: MerchantInfoProps) {
  const { t } = useTranslation('merchants');

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('detail.info')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoField label={t('detail.fields.merchantId')}>
            <p className="font-mono">{merchant.merchantId}</p>
          </InfoField>
          <InfoField label={t('detail.fields.name')}>
            <p className="font-medium">{merchant.name}</p>
          </InfoField>
          <InfoField label={t('detail.fields.status')}>
            <Badge variant={statusVariant[merchant.status]}>
              {t(`statuses.${merchant.status}`)}
            </Badge>
          </InfoField>
          <InfoField label={t('detail.fields.contactEmail')}>
            <p>{merchant.contactEmail ?? '—'}</p>
          </InfoField>
          <InfoField label={t('detail.fields.createdAt')}>
            <p>{formatDate(merchant.createdAt)}</p>
          </InfoField>
          <InfoField label={t('detail.fields.updatedAt')}>
            <p>{formatDate(merchant.updatedAt)}</p>
          </InfoField>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('detail.integration')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoField label={t('detail.fields.incidentEmail')}>
            <p>{merchant.incidentEmail ?? '—'}</p>
          </InfoField>
          <InfoField label={t('detail.fields.customerId')}>
            <p className="font-mono text-sm">{merchant.customerId ?? '—'}</p>
          </InfoField>
          <InfoField label={t('detail.fields.serviceCount')}>
            <p>{merchant.serviceCount}</p>
          </InfoField>
          <InfoField label={t('detail.fields.activeSubscriptionCount')}>
            <p>{merchant.activeSubscriptionCount}</p>
          </InfoField>
          <InfoField label={t('detail.fields.hasWebhook')}>
            <Badge variant={merchant.hasWebhook ? 'success' : 'outline'}>
              {merchant.hasWebhook ? t('common:yes') : t('common:no')}
            </Badge>
          </InfoField>
        </CardContent>
      </Card>
    </div>
  );
}
